import { useState, useEffect, useCallback } from 'react';
import { useAppStore, recalculateStore } from '@/store/useAppStore';
import {
  listProfiles,
  createProfile,
  deleteProfile,
  renameProfile,
  switchProfile,
  getActiveProfileId,
  ensureDefaultProfile,
  type StudentProfile,
} from '@/store/profileManager';

export function ProfileSwitcher() {
  const [profiles, setProfiles] = useState<StudentProfile[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmSwitchId, setConfirmSwitchId] = useState<string | null>(null);

  const courses = useAppStore((s) => s.courses);
  const settings = useAppStore((s) => s.settings);

  // Close dropdown on outside click (R19)
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-profile-dropdown]')) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen]);

  const refresh = useCallback(async () => {
    let all = await listProfiles();
    if (all.length === 0) {
      // Initialize default profile if needed
      await ensureDefaultProfile();
      all = await listProfiles();
    }
    setProfiles(all);
    setActiveId(await getActiveProfileId());
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleCreate = useCallback(async () => {
    if (!newName.trim()) return;
    await createProfile(newName.trim());
    setNewName('');
    await refresh();
  }, [newName, refresh]);

  const handleSwitch = useCallback(
    async (id: string) => {
      const data = await switchProfile(id, courses, settings);
      if (data) {
        useAppStore.getState().importCourses(data.courses);
        useAppStore.getState().loadSettings(data.settings);
      } else {
        useAppStore.getState().importCourses([]);
      }
      recalculateStore();
      setActiveId(id);
      setIsOpen(false);
    },
    [courses, settings]
  );

  const handleRename = useCallback(
    async (id: string) => {
      if (!editName.trim()) return;
      await renameProfile(id, editName.trim());
      setEditingId(null);
      setEditName('');
      await refresh();
    },
    [editName, refresh]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      await deleteProfile(id);
      setConfirmDeleteId(null);
      await refresh();
      // If we deleted the active profile, switch to the first remaining
      if (id === activeId) {
        const remaining = await listProfiles();
        if (remaining.length > 0) {
          await handleSwitch(remaining[0].id);
        }
      }
    },
    [activeId, refresh, handleSwitch]
  );

  const activeProfile = profiles.find((p) => p.id === activeId);

  return (
    <div className="relative" data-profile-dropdown>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex max-w-[12rem] items-center gap-2 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]"
        aria-label="Switch profile"
        aria-expanded={isOpen}
        data-testid="btn-profile-toggle"
      >
        <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
        <span className="allow-shrink truncate">{activeProfile?.name ?? 'Profile'}</span>
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full z-50 mt-1 w-64 rounded-lg border border-border bg-surface shadow-lg">
          <div className="border-b border-border p-3">
            <p className="text-xs font-semibold text-[var(--color-text)]">Student Profiles</p>
          </div>

          <div className="max-h-48 overflow-y-auto p-2">
            {profiles.map((profile) => (
              <div key={profile.id} className="group flex items-center justify-between rounded px-2 py-1.5">
                {editingId === profile.id ? (
                  <div className="flex flex-1 items-center gap-1">
              <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleRename(profile.id)}
                  className="allow-shrink flex-1 rounded border border-border px-2 py-1 text-xs"
                  maxLength={80}
                  autoFocus
                  data-testid="input-profile-rename"
              />
              <button onClick={() => handleRename(profile.id)} className="text-xs text-primary" data-testid="btn-profile-save">
                      Save
                    </button>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        if (profile.id === activeId) return;
                        setConfirmSwitchId(profile.id);
                      }}
                      className={`allow-shrink flex-1 truncate text-left text-xs ${
                        profile.id === activeId
                          ? 'font-semibold text-primary'
                          : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text)]'
                      }`}
                      title={profile.name}
                    >
                      {profile.name}
                      {profile.id === activeId && ' (active)'}
                    </button>
                    {confirmSwitchId === profile.id && (
                      <div className="absolute left-0 top-full z-50 mt-1 w-56 rounded-lg border border-border bg-surface p-3 shadow-lg">
                        <p className="mb-2 text-xs text-[var(--color-text)]">
                          Switch to <strong>{profile.name}</strong>? Current work will be saved.
                        </p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              handleSwitch(profile.id);
                              setConfirmSwitchId(null);
                            }}
                            className="rounded-md bg-primary px-3 py-1 text-xs text-white"
                          >
                            Switch
                          </button>
                          <button
                            onClick={() => setConfirmSwitchId(null)}
                            className="rounded-md border border-border px-3 py-1 text-xs text-[var(--color-text-secondary)]"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                      <button
                        onClick={() => {
                          setEditingId(profile.id);
                          setEditName(profile.name);
                        }}
                        className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                      >
                        Edit
                      </button>
                       {confirmDeleteId === profile.id ? (
                          <>
                            <button
                              onClick={() => handleDelete(profile.id)}
                              className="text-xs text-error"
                            >
                              Delete
                            </button>
                            <button
                              onClick={() => setConfirmDeleteId(null)}
                              className="text-xs text-[var(--color-text-muted)]"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => setConfirmDeleteId(profile.id)}
                            className="text-xs text-error"
                          >
                            Delete
                          </button>
                        )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          <div className="border-t border-border p-2">
            <div className="flex items-center gap-1">
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                placeholder="New profile name"
                className="allow-shrink flex-1 rounded border border-border px-2 py-1 text-xs"
                maxLength={80}
                data-testid="input-new-profile"
              />
              <button
                onClick={handleCreate}
                className="rounded bg-primary px-2 py-1 text-xs text-white"
                data-testid="btn-create-profile"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
